import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { UserPlus, Filter, Search, Trash2, ClipboardList } from "lucide-react";
import { DEPARTMENTS, DEPT_LABEL, POSITION_CLS } from "@/lib/teamCatalog";
import RaiseHiringRequestDialog from "@/components/RaiseHiringRequestDialog";
import { CardBlockSkeleton } from "@/components/LoadingSkeleton";

const STATUS_META = {
  open:        { label: "Open",        cls: "bg-amber-50 text-amber-700 border-amber-200" },
  in_progress: { label: "In Progress", cls: "bg-blue-50 text-blue-700 border-blue-200" },
  filled:      { label: "Filled",      cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  closed:      { label: "Closed",      cls: "bg-slate-100 text-slate-600 border-slate-200" },
};

export default function HiringRequests() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterDept, setFilterDept] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [search, setSearch] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [showCreate, setShowCreate] = useState(false);

  const isHR = (user?.department || "").toLowerCase() === "hr";
  const isAdmin = (user?.role === "admin" || user?.role === "super_admin");
  const canManage = isHR || isAdmin;
  const canRaise = user && (["admin", "content_manager", "team_lead"].includes(user.role) || isHR);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/hiring-requests");
      setItems(data || []);
    } catch (e) { toast.error(formatApiError(e)); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    let list = items;
    if (filterDept !== "all") list = list.filter((r) => r.department === filterDept);
    if (filterStatus !== "all") list = list.filter((r) => (r.status || "open") === filterStatus);
    const q = search.trim().toLowerCase();
    if (q) {
      list = list.filter((r) => {
        if (r.position.toLowerCase().includes(q)) return true;
        if ((r.reason || "").toLowerCase().includes(q)) return true;
        if ((r.replacing_user_name || "").toLowerCase().includes(q)) return true;
        if ((r.requested_by_names || []).some((n) => n.toLowerCase().includes(q))) return true;
        return false;
      });
    }
    return list;
  }, [items, filterDept, filterStatus, search]);

  const grouped = useMemo(() => {
    const g = {};
    for (const r of filtered) {
      const d = r.department || "other";
      (g[d] = g[d] || []).push(r);
    }
    return g;
  }, [filtered]);

  const setStatus = async (r, status) => {
    setItems((prev) => prev.map((x) => (x.id === r.id ? { ...x, status } : x)));
    try {
      await api.patch(`/hiring-requests/${r.id}`, { status });
    } catch (e) {
      toast.error(formatApiError(e));
      setItems((prev) => prev.map((x) => (x.id === r.id ? r : x)));
    }
  };

  const doDelete = async () => {
    if (!confirmDelete) return;
    try {
      await api.delete(`/hiring-requests/${confirmDelete.id}`);
      toast.success("Hiring request deleted");
      setItems((prev) => prev.filter((x) => x.id !== confirmDelete.id));
      setConfirmDelete(null);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="max-w-[1400px] mx-auto p-6 space-y-5">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-3xl font-bold text-slate-900 flex items-center gap-2"><ClipboardList className="h-7 w-7 text-rose-600" /> Hiring Requests</h1>
          <p className="text-sm text-slate-500 mt-0.5">
            {canManage ? "All requests across departments." : "Requests you or your team have raised."}
          </p>
        </div>
        {canRaise && (
          <Button className="bg-rose-600 hover:bg-rose-700" onClick={() => setShowCreate(true)} data-testid="raise-hiring-btn">
            <UserPlus className="h-4 w-4 mr-1.5" /> Add request
          </Button>
        )}
      </div>

      {/* Filters */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[240px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search position, requester, note…" className="pl-9" data-testid="hr-search" />
        </div>
        <Select value={filterDept} onValueChange={setFilterDept}>
          <SelectTrigger className="w-40 h-9" data-testid="hr-filter-dept"><div className="flex items-center gap-2"><Filter className="h-3.5 w-3.5" /><SelectValue /></div></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All departments</SelectItem>
            {DEPARTMENTS.map((d) => <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-36 h-9" data-testid="hr-filter-status"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {Object.entries(STATUS_META).map(([k, m]) => <SelectItem key={k} value={k}>{m.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="ml-auto text-xs text-slate-500">{filtered.length} of {items.length}</div>
      </div>

      {loading && <CardBlockSkeleton count={4} />}

      {!loading && filtered.length === 0 && (
        <Card className="border-dashed border-slate-300 bg-slate-50/40 py-10 text-center">
          <ClipboardList className="h-10 w-10 text-slate-300 mx-auto mb-2" />
          <p className="text-sm text-slate-600 font-medium">No hiring requests yet</p>
          <p className="text-xs text-slate-400 mt-1">Team leads or content managers can raise a request when someone leaves or the team needs to grow.</p>
        </Card>
      )}

      {Object.entries(grouped).map(([dept, list]) => (
        <div key={dept}>
          <h2 className="font-display text-sm font-semibold text-slate-700 mb-2 uppercase tracking-wider">
            {DEPT_LABEL[dept] || dept} <span className="text-slate-400 font-normal">· {list.length}</span>
          </h2>
          <Card className="overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="text-left px-4 py-2 text-[11px] font-semibold uppercase tracking-wider text-slate-500">Position</th>
                  <th className="text-left px-4 py-2 text-[11px] font-semibold uppercase tracking-wider text-slate-500">Requested by</th>
                  <th className="text-left px-4 py-2 text-[11px] font-semibold uppercase tracking-wider text-slate-500">Replacing</th>
                  <th className="text-left px-4 py-2 text-[11px] font-semibold uppercase tracking-wider text-slate-500">Note</th>
                  <th className="text-left px-4 py-2 text-[11px] font-semibold uppercase tracking-wider text-slate-500">Raised</th>
                  <th className="text-left px-4 py-2 text-[11px] font-semibold uppercase tracking-wider text-slate-500">Status</th>
                  <th className="w-14"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {list.map((r) => {
                  const posCls = POSITION_CLS[r.position] || "bg-slate-100 text-slate-700 border-slate-200";
                  const status = r.status || "open";
                  const statusMeta = STATUS_META[status] || STATUS_META.open;
                  return (
                    <tr key={r.id} className="hover:bg-slate-50" data-testid={`hr-row-${r.id}`}>
                      <td className="px-4 py-3"><Badge className={`${posCls} border`}>{r.position}</Badge></td>
                      <td className="px-4 py-3 text-xs text-slate-700">{(r.requested_by_names || []).join(", ")}</td>
                      <td className="px-4 py-3 text-xs text-slate-700">{r.replacing_user_name || <span className="text-slate-300">—</span>}</td>
                      <td className="px-4 py-3 text-xs text-slate-600 max-w-[240px]">{r.reason || <span className="text-slate-300 italic">—</span>}</td>
                      <td className="px-4 py-3 text-[11px] text-slate-500 whitespace-nowrap">{new Date(r.created_at).toLocaleDateString()}</td>
                      <td className="px-4 py-3">
                        {canManage ? (
                          <Select value={status} onValueChange={(v) => setStatus(r, v)}>
                            <SelectTrigger className={`h-7 text-[11px] w-32 ${statusMeta.cls}`} data-testid={`hr-status-${r.id}`}><SelectValue /></SelectTrigger>
                            <SelectContent>
                              {Object.entries(STATUS_META).map(([k, m]) => <SelectItem key={k} value={k}>{m.label}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        ) : (
                          <Badge className={`${statusMeta.cls} border`}>{statusMeta.label}</Badge>
                        )}
                      </td>
                      <td className="px-2 py-3 text-right">
                        {(canManage || (r.requested_by_ids || []).includes(user?.id)) && (
                          <button onClick={() => setConfirmDelete(r)} className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded" data-testid={`hr-delete-${r.id}`}>
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </Card>
        </div>
      ))}

      {showCreate && <RaiseHiringRequestDialog open={showCreate} onClose={() => setShowCreate(false)} onSaved={load} />}

      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete hiring request?</AlertDialogTitle>
            <AlertDialogDescription>This will remove the request for <strong>{confirmDelete?.position}</strong> in <strong>{DEPT_LABEL[confirmDelete?.department]}</strong>.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
